import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { ChatMessage, ChatState } from "../types.js";
import type { ChatHomeTile } from "../home-tiles.js";
import type { ChatSuggestion } from "./jonas-suggestions.js";
import type { HelpJonasChatComposer } from "./jonas-chat-composer.js";
import { DEFAULT_MAX_MESSAGE_LENGTH } from "../config.js";
import { createHomeTileClickReporter } from "../tag-manager.js";
import "./jonas-availability-banner.js";
import "./jonas-fallback-panel.js";
import {
  getLiveFallbackMessage,
  isReconnectingAfterFallback,
  isThreadInFallbackMode,
} from "../fallback.js";

const DEFAULT_DISCLAIMER_TEXT = "Let op: Jonas kan fouten maken";
// When a freshly-appeared AI-unavailable fallback is brought into view, leave
// this many pixels of the previous complete answer visible at the top so the
// last Q&A stays in context above the fallback panel. The live streaming answer
// itself follows to the bottom of the thread instead (see followAnswerToBottom).
const PREVIOUS_ANSWER_VISIBLE_TAIL_PX = 25;
const THREAD_SCROLL_STORAGE_KEY = "help.aiChatThreadScroll";
// Bump the version to bring the privacy notice back after a material wording change.
const PRIVACY_NOTICE_STORAGE_KEY = "help-jonas:privacy-notice-dismissed-v2";
// Covers the follow-up entrance animation plus its per-row stagger.
const SUGGESTION_ENTER_MS = 380;
// How far from the bottom the visitor may be for arriving follow-ups to still be nudged
// into view. Comfortably more than the block itself, so being pushed down by its own
// arrival still counts as "was reading the end of the answer".
const FOLLOW_UP_REVEAL_MAX_PX = 240;

/** Mirrors COMPACT_MAX_WIDTH in jonas-chat-appbar.ts: the workspace's narrow layout. */
const COMPACT_MAX_WIDTH = 760;

// Where the answer text starts, measured from the message anchor: the identicon column,
// the bubble's accent border and its padding. The column is gone below COMPACT_MAX_WIDTH
// (see jonas-chat-message.ts), so the disclaimer and the arrows move with it.
const ANSWER_TEXT_INSET = 60;
const ANSWER_TEXT_INSET_COMPACT = 20;
// The arrows sit 8px further in than their inset: their button carries that padding.
const FOLLOW_UP_BUTTON_PADDING = 8;

@customElement("help-jonas-chat-thread")
export class HelpJonasChatThread extends LitElement {
  static override styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
      background: var(--color-background-900);
    }

    .thread {
      display: flex;
      height: 100%;
      min-height: 0;
      flex-direction: column;
      background: var(--color-background-800);
    }

    /*
     * The composer must never shrink below its content height. With the default
     * flex: 0 1 auto it can be compressed when the message list competes for
     * space, which pushes the composer's pending-attachment row off the bottom of
     * the viewport. Pinning flex-shrink to 0 keeps the composer (and its
     * attachments) fully visible; the scrollable .messages region absorbs the
     * space instead.
     */
    help-jonas-chat-composer {
      flex: 0 0 auto;
    }

    .messages {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      /*
       * Take full ownership of the scroll position during answer generation. The
       * auto-follow drives scrollTop programmatically; with the browser default
       * overflow-anchor: auto the browser would ALSO shift scrollTop whenever
       * content above the viewport changes (an earlier answer collapsing, late
       * reflow), fighting the follow and shoving the streaming answer around.
       * Disabling anchoring keeps every scrollTop change attributable to our own
       * eased follow or a real user gesture; the ResizeObserver re-pins the
       * anchor after reflow.
       */
      overflow-anchor: none;
      /*
       * Reserve the scrollbar on both sides, so the centred column lands in the same
       * place as the composer's. Without it the scrollbar eats 10px on the right only,
       * which shifts every message 5px left of the input and the questions under it --
       * and shifts them again the moment a conversation grows long enough to scroll.
       */
      scrollbar-gutter: stable both-edges;
      /*
       * The bottom padding is scrollable room, not just margin: it keeps the newest turn
       * and the questions it offers clear of the composer, and gives the thread somewhere
       * to move when the next question is asked instead of snapping tight to the input.
       */
      padding: 48px 40px 64px;
      scrollbar-width: thin;
      scrollbar-color: var(--color-black-400) transparent;
    }

    .messages::-webkit-scrollbar {
      width: 6px;
      height: 6px;
      background-color: transparent;
    }

    .messages::-webkit-scrollbar-track {
      background-color: transparent;
    }

    .messages::-webkit-scrollbar-thumb {
      background-color: var(--color-black-400);
      background-clip: padding-box;
      border-radius: 4px;
    }

    .messages::-webkit-scrollbar-thumb:hover {
      background-color: var(--color-black-500);
    }

    .thread.is-initial {
      justify-content: center;
    }

    .thread.is-initial .messages {
      flex: 0 0 auto;
      overflow: visible;
      padding: 0 24px 12px;
    }

    .thread.is-initial .messages-inner {
      display: grid;
      place-items: center;
    }

    .thread.has-conversation .messages {
      overflow: auto;
    }

    .messages-inner {
      max-width: 960px;
      margin: 0 auto;
    }

    .message-anchor {
      display: grid;
      scroll-margin-block-start: 24px;
    }

    .message-anchor.has-disclaimer help-jonas-chat-message {
      margin-bottom: 8px;
    }

    /* Lines up with the text of the answer above. */
    .follow-ups,
    .follow-up-skeleton {
      margin-top: 8px;
      padding-left: ${ANSWER_TEXT_INSET - FOLLOW_UP_BUTTON_PADDING}px;
    }

    .follow-up-skeleton {
      display: flex;
      pointer-events: none;
    }

    /*
     * Left-aligned on the answer text and the arrows below it, not centred: a centred
     * disclaimer under a one-line answer reads as a caption for the whole column. The
     * top padding clears the message's hover chip, which floats 13px below the bubble;
     * the larger bottom padding keeps this a footnote to the answer rather than a
     * heading for the follow-ups.
     */
    .disclaimer {
      justify-self: start;
      margin-left: ${ANSWER_TEXT_INSET}px;
      padding-top: 8px;
      padding-bottom: 16px;
      user-select: none;
      opacity: 0.5;
    }

    @media (max-width: ${COMPACT_MAX_WIDTH}px) {
      .disclaimer {
        margin-left: ${ANSWER_TEXT_INSET_COMPACT}px;
      }

      .follow-ups,
      .follow-up-skeleton {
        padding-left: ${ANSWER_TEXT_INSET_COMPACT -
        FOLLOW_UP_BUTTON_PADDING}px;
      }
    }

    /*
     * Notices that belong to the composer rather than to the conversation sit directly
     * above it, in the composer's own column: the composer insets itself by 24px and caps
     * its content at 960px, so matching that puts their edges on the same lines.
     */
    .chat-notice {
      flex: 0 0 auto;
      width: min(100% - 48px, 960px);
      margin: 0 auto 8px;
    }

    .chat-notice::part(base) {
      margin: 0;
    }

    .empty {
      display: grid;
      min-height: 42vh;
      place-self: center;
      place-items: center;
      color: var(--color-foreground-700);
      font-size: 0.8em;
      text-align: center;
      user-select: none;
      opacity: 0.5;
    }

    .prepared-question {
      display: grid;
      min-height: 42vh;
      place-items: center;
      color: var(--color-text-on-background-800);
    }

    .prepared-question__content {
      display: grid;
      width: min(100%, 680px);
      gap: 16px;
      justify-items: start;
      border: 1px solid var(--color-foreground-300);
      border-radius: 8px;
      background: var(--color-background-800);
      padding: 20px;
    }

    .prepared-question__text {
      margin: 0;
      line-height: 1.5;
      overflow-wrap: anywhere;
    }

    /*
     * Print (Ctrl+P). The thread prints in place via these media-only rules
     * (no JS controller). They let the conversation flow across pages (no inner
     * scroll/clamped height) and drop the interactive workspace chrome that
     * lives inside the thread — the composer (which carries the suggestions) and
     * the status line — so only the message thread prints. (Per-message controls
     * are hidden by the message component's own print rules.)
     */
    @media print {
      :host {
        height: auto;
        min-height: 0;
        background: transparent;
      }

      /*
       * Switch the thread, message list and per-message anchors from flex/grid
       * to block layout for paper. A display:flex/grid container taller than one
       * printed page does NOT fragment across pages in Chromium — it clips the
       * overflow to the first page, silently dropping the trailing message(s).
       * Block layout paginates cleanly, so the whole conversation flows across
       * pages. (justify-content becomes an inert no-op under block; kept
       * harmless to avoid churn.)
       */
      .thread,
      .thread.is-initial,
      .thread.has-conversation {
        display: block;
        height: auto;
        justify-content: flex-start;
      }

      .messages,
      .thread.is-initial .messages,
      .thread.has-conversation .messages {
        display: block;
        height: auto;
        padding: 0;
        overflow: visible;
      }

      .message-anchor {
        display: block;
      }

      .messages-inner {
        max-width: 100%;
      }

      help-jonas-chat-composer,
      help-jonas-status {
        display: none !important;
      }

      /*
       * A Jonas answer is frequently TALLER than one printed page. With
       * break-inside: avoid, a message that does not fit in the space left on the
       * current page is pushed WHOLE onto the next page rather than split — which
       * leaves a full page of trailing whitespace after the previous message (and
       * in some browsers clips the overflow, dropping the tail). Let messages
       * paginate so the conversation flows continuously across pages with no gaps
       * and nothing lost. (Verified via CDP printToPDF on real Edge: avoid = 3
       * pages with a blank page-1 tail; auto = 2 pages, content flows immediately.)
       */
      help-jonas-chat-message {
        break-inside: auto;
      }
    }
  `;

  @property({ attribute: false })
  accessor state: ChatState | null = null;

  @property({ attribute: false })
  accessor homeTiles: ChatHomeTile[] = [];

  @property()
  accessor emptyLabel = "Waar kan Jonas mee helpen?";

  @property()
  accessor preparedQuestionActionLabel = "Vraag stellen";

  @property()
  accessor submitLabel = "Verstuur";

  @property()
  accessor promptPlaceholder = "Stel je vraag aan Jonas";

  @property()
  accessor stopLabel = "Stop";

  private userInterruptedAnswerScroll = false;

  private answerScrollFrame = 0;

  private contentResizeObserver: ResizeObserver | null = null;

  private restoreScrollFrame = 0;

  private pagehideHandler: (() => void) | null = null;

  private restoredScrollSessionToken = "";

  // The conversation this thread instance has settled its scroll on. Empty until the
  // first real conversation lands, and never updated while a resume is still loading
  // (the transient empty session token), so a genuine in-page switch is detected on the
  // id that actually arrives. Drives resetScrollOnConversationSwitch (issue #116).
  private lastConversationToken = "";

  // Auto-scroll bookkeeping for the AI-unavailable Jonas Search fallback. A live
  // fallback message arrives with status "idle" and pending false, so the normal
  // generating-only follow never reveals it. We scroll it into view once, the same
  // way an answer is, when it APPEARS after a failed question.
  // fallbackScrollHandledId guards against re-scrolling on later re-renders
  // (refreshing the panel's sources, the availability banner, the silent reconnect
  // probe) so the user can read the Help articles without the view jumping back.
  // sawContentBeforeFallback is false on a fresh page load whose first populated
  // render is a restored fallback (a new component instance), so a refresh keeps the
  // user's saved scroll position (restoreThreadScrollAfterRefresh owns it) instead
  // of jumping.
  private fallbackScrollHandledId = "";

  private sawContentBeforeFallback = false;

  private composerFocusRequest = 0;

  // Tracks the previous streaming state so the composer can be re-focused the
  // moment an answer finishes streaming (see updateComposerFocusRequest, #142).
  private wasStreaming = false;

  private wasInitial = true;
  private readonly homeTileClicks = createHomeTileClickReporter(window);

  private isTransitioningFromInitial = false;

  private composerFirstBottom = 0;

  // Whether the visitor closed the privacy notice earlier this browser session.
  @state() private accessor privacyNoticeDismissed =
    readPrivacyNoticeDismissed();

  // Set for the length of the animation when the follow-ups replace their placeholder.
  @state() private accessor animateFollowUps = false;
  private followUpEnterTimer: ReturnType<typeof setTimeout> | null = null;
  private wasFollowUpsPending = false;
  private followUpsJustArrived = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.pagehideHandler = () => this.saveThreadScroll();
    window.addEventListener("pagehide", this.pagehideHandler);
  }

  protected override willUpdate(): void {
    this.updateComposerFocusRequest();
    this.updateFollowUpEntrance();
  }

  protected override updated(): void {
    this.ensureContentResizeObserver();
    this.trackContentBeforeFallback();
    this.schedulePendingAnswerScroll();
    this.restoreThreadScrollAfterRefresh();
    this.resetScrollOnConversationSwitch();
    this.handleTransitionFromInitialAnimation();
    this.revealArrivedFollowUps();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.answerScrollFrame !== 0) {
      window.cancelAnimationFrame(this.answerScrollFrame);
      this.answerScrollFrame = 0;
    }
    if (this.restoreScrollFrame !== 0) {
      window.cancelAnimationFrame(this.restoreScrollFrame);
      this.restoreScrollFrame = 0;
    }
    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
    }
    if (this.pagehideHandler) {
      window.removeEventListener("pagehide", this.pagehideHandler);
      this.pagehideHandler = null;
    }
  }

  protected override render() {
    const messages = this.state?.messages ?? [];
    const inFallback = isThreadInFallbackMode(messages);
    const status = this.state?.status ?? "idle";
    const reconnecting = isReconnectingAfterFallback(messages, status);
    const streaming = status === "starting" || status === "streaming";
    const busy = streaming || this.state?.isRestoring === true;
    const preparedQuestion = this.state?.preparedQuestion ?? null;
    const isInitial = this.isInitialChat();

    return html`
      <div class=${`thread ${isInitial ? "is-initial" : "has-conversation"}`}>
        ${inFallback || reconnecting
          ? html`<help-jonas-availability-banner
              .labels=${this.state?.labels ?? null}
              .restored=${reconnecting}
              .reconnecting=${this.state?.reconnecting ?? false}
            ></help-jonas-availability-banner>`
          : nothing}
        <div
          class="messages"
          @wheel=${this.handleMessagesUserScroll}
          @touchstart=${this.handleMessagesUserScroll}
          @keydown=${this.handleMessagesUserScroll}
          @pointerdown=${this.handleMessagesUserScroll}
        >
          <div class="messages-inner">
            ${isInitial
              ? nothing
              : messages.length === 0
                ? this.renderPreparedQuestion(preparedQuestion, busy)
                : messages.map((message, index) =>
                    this.renderMessage(
                      message,
                      this.shouldShowDisclaimer(message, index, messages),
                      streaming && message.fallback?.frozen === false,
                      this.shouldClampMessage(message, index, messages),
                      index === messages.length - 1,
                    ),
                  )}
            <help-jonas-status
              status=${this.state?.status ?? "idle"}
              error=${this.state?.error ?? ""}
              retryQuestion=${this.state?.retryQuestion ?? ""}
              retryLabel=${this.getLabel(
                "resumeRetryAction",
                "Vraag opnieuw stellen",
              )}
            ></help-jonas-status>
          </div>
        </div>
        ${reconnecting
          ? html`<afas-alert
              class="chat-notice"
              type="info"
              display-style="compact"
              role="status"
              aria-live="polite"
              >${this.getLabel(
                "reaskNotice",
                "Je laatste vraag wordt opnieuw aan Jonas (AI) gesteld…",
              )}</afas-alert
            >`
          : nothing}
        <help-jonas-chat-composer
          .focusRequest=${this.composerFocusRequest}
          .initial=${isInitial}
          .busy=${busy}
          .showStop=${streaming}
          .showNotice=${this.isPrivacyNoticeVisible()}
          @jonas-notice-dismiss=${this.dismissPrivacyNotice}
          @jonas-question-submit=${this.trackHomeTileClick}
          .labels=${this.state?.labels ?? null}
          .maxMessageLength=${this.state?.maxMessageLength ??
          DEFAULT_MAX_MESSAGE_LENGTH}
          .suggestions=${isInitial ? this.getHomeSuggestions() : []}
          .submitLabel=${this.getLabel("sendButton", this.submitLabel)}
          .placeholder=${this.getLabel(
            "promptPlaceholder",
            this.promptPlaceholder,
          )}
          .stopLabel=${this.getLabel("stopButton", this.stopLabel)}
          .inputAriaLabel=${this.getLabel("inputAriaLabel", "Vraag aan Jonas")}
          .locked=${inFallback}
          .lockedHint=${this.getLabel(
            "composerLockedHint",
            "Jonas (AI) niet beschikbaar — zoek in Help of probeer opnieuw",
          )}
        ></help-jonas-chat-composer>
      </div>
    `;
  }

  // The CMS tile carries a title for the editor and a description for the visitor; the
  // description is what reads as a question to ask.
  private getHomeSuggestions(): ChatSuggestion[] {
    return this.homeTiles.map((tile) => ({
      id: tile.id,
      label: tile.description,
      question: tile.question,
    }));
  }

  // Only the home suggestions carry an id (the CMS tile id); a typed question and a
  // follow-up do not, so neither is ever reported. The event keeps bubbling to the app.
  private trackHomeTileClick(
    event: CustomEvent<{ suggestionId?: string | number }>,
  ): void {
    const tileId = event.detail?.suggestionId;
    if (tileId === undefined) return;
    const tile = this.homeTiles.find((candidate) => candidate.id === tileId);
    if (tile) this.homeTileClicks.report(tile);
  }

  private updateComposerFocusRequest(): void {
    const isInitial = this.isInitialChat();
    if (isInitial && !this.wasInitial) this.composerFocusRequest += 1;
    // Back on the home screen: its next tile click counts again.
    if (isInitial && !this.wasInitial) this.homeTileClicks.reset();

    // When an answer finishes streaming, re-focus the composer so the visitor can
    // type the next question straight away (#142). Skipped while the composer is
    // locked (AI-unavailable fallback) or on the Home/initial screen (handled
    // above). Keyed on `streaming` only, so a page-load history restore
    // (isRestoring) never steals focus on hydration.
    const status = this.state?.status ?? "idle";
    const streaming = status === "starting" || status === "streaming";
    const locked = isThreadInFallbackMode(this.state?.messages ?? []);
    if (this.wasStreaming && !streaming && !isInitial && !locked) {
      this.composerFocusRequest += 1;
    }
    this.wasStreaming = streaming;

    if (!isInitial && this.wasInitial && this.isLiveHomeTransition()) {
      this.isTransitioningFromInitial = true;
      const composer = this.shadowRoot?.querySelector<HelpJonasChatComposer>(
        "help-jonas-chat-composer",
      );
      if (composer) {
        this.composerFirstBottom = composer.inputBottom();
      }
    } else {
      this.isTransitioningFromInitial = false;
    }

    this.wasInitial = isInitial;
  }

  // The composer only slides from the centered Home position down to the bottom
  // for a genuine, user-initiated Home -> conversation transition: a live ask
  // (status starting/streaming) or a freshly prepared question. A refresh or a
  // history restore hydrates an existing conversation after a transient Home
  // render; that is flagged by isRestoring (history/pending resume) or arrives as
  // an idle, fully-formed conversation, so it must NOT animate.
  private isLiveHomeTransition(): boolean {
    const state = this.state;
    if (!state || state.isRestoring === true) return false;
    return (
      state.status === "starting" ||
      state.status === "streaming" ||
      !!state.preparedQuestion
    );
  }

  private handleTransitionFromInitialAnimation(): void {
    if (!this.isTransitioningFromInitial) return;

    this.isTransitioningFromInitial = false;

    const composer = this.shadowRoot?.querySelector<HelpJonasChatComposer>(
      "help-jonas-chat-composer",
    );
    if (!composer || this.composerFirstBottom <= 0) return;

    const firstBottom = this.composerFirstBottom;
    this.composerFirstBottom = 0;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) return;

    const deltaY = composer.inputBottom() - firstBottom;
    if (deltaY <= 0) return;

    composer.style.setProperty("transition", "none");
    composer.style.setProperty("transform", `translateY(${-deltaY}px)`);

    void composer.offsetHeight; // Force reflow

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        composer.style.removeProperty("transition");
        composer.style.removeProperty("transform");
      });
    });
  }

  private isInitialChat(): boolean {
    const messages = this.state?.messages ?? [];
    return messages.length === 0 && !this.state?.preparedQuestion;
  }

  // True while the last assistant turn is finalizing (visible answer settled) but its
  // follow-ups have not arrived yet. A placeholder then holds the row they land in, so
  // the follow-ups do not pop in abruptly at completion.
  private getFollowUpsPending(messages: ChatMessage[]): boolean {
    const last = messages[messages.length - 1];
    return (
      last?.role === "assistant" &&
      last.finalizing === true &&
      last.followUps.length === 0
    );
  }

  /**
   * Marks the swap from placeholder to real follow-ups so they animate in once. The
   * marker has to outlive the render that starts it: an answer settling produces more
   * renders right after, and dropping it again would cut the animation off before the
   * later rows have even begun.
   */
  private updateFollowUpEntrance(): void {
    const messages = this.state?.messages ?? [];
    const pending = this.getFollowUpsPending(messages);
    const arrived =
      this.wasFollowUpsPending &&
      !pending &&
      (messages[messages.length - 1]?.followUps.length ?? 0) > 0;
    this.wasFollowUpsPending = pending;
    if (!arrived) return;

    this.followUpsJustArrived = true;
    if (this.followUpEnterTimer) clearTimeout(this.followUpEnterTimer);
    this.animateFollowUps = true;
    this.followUpEnterTimer = setTimeout(() => {
      this.followUpEnterTimer = null;
      this.animateFollowUps = false;
    }, SUGGESTION_ENTER_MS);
  }

  /**
   * The follow-ups land after the auto-follow has let go of the answer, so without this
   * they would sit just under the fold of a long answer. Only nudges when the visitor was
   * already reading the end of the answer; someone who scrolled up to re-read something
   * keeps their place.
   */
  private revealArrivedFollowUps(): void {
    if (!this.followUpsJustArrived) return;
    this.followUpsJustArrived = false;

    requestAnimationFrame(() => {
      const messagesElement = this.getMessagesElement();
      if (!messagesElement || this.isGenerating()) return;

      const distanceFromBottom =
        messagesElement.scrollHeight -
        messagesElement.clientHeight -
        messagesElement.scrollTop;
      if (distanceFromBottom > FOLLOW_UP_REVEAL_MAX_PX) return;

      messagesElement.scrollTo({
        top: this.clampScrollTop(messagesElement, messagesElement.scrollHeight),
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    });
  }

  private renderMessage(
    message: ChatMessage,
    showDisclaimer: boolean,
    reconnecting = false,
    clamped = false,
    isLast = false,
  ) {
    return html`<div
      class=${`message-anchor ${showDisclaimer ? "has-disclaimer" : ""}`}
      data-message-id=${message.id}
      data-message-role=${message.role}
      data-message-pending=${message.pending ? "true" : "false"}
    >
      ${message.fallback
        ? html`<help-jonas-fallback-panel
            .message=${message}
            .labels=${this.state?.labels ?? null}
            .reconnecting=${reconnecting}
          ></help-jonas-fallback-panel>`
        : html`<help-jonas-chat-message
            .message=${message}
            .labels=${this.state?.labels ?? null}
            .clamped=${clamped}
            .feedbackPending=${this.state?.feedbackPendingId === message.id}
          ></help-jonas-chat-message>`}
      ${showDisclaimer ? this.renderDisclaimer() : null}
      ${isLast ? this.renderFollowUps(message) : null}
    </div>`;
  }

  /**
   * The questions Jonas offers next are about the answer they follow, so they are part of
   * it: they sit under the answer in the scroll area and stay there when the visitor
   * scrolls back up, rather than riding along above the composer.
   */
  private renderFollowUps(message: ChatMessage) {
    if (message.role !== "assistant" || message.fallback || message.canceled) {
      return null;
    }

    if (message.followUps.length) {
      return html`<help-jonas-suggestions
        class="follow-ups"
        .suggestions=${message.followUps.map((followUp) => ({
          label:
            followUp.label || this.getLabel("followUpFallback", "Vervolgvraag"),
          question: followUp.question,
        }))}
        .disabled=${this.isGenerating()}
        .entrance=${this.animateFollowUps}
        listLabel=${this.getLabel("followUpsTitle", "Vervolgvragen")}
      ></help-jonas-suggestions>`;
    }

    // Only while the answer has settled but its follow-ups are still being written: the
    // placeholder holds the row they land in, so the swap does not shift the layout.
    if (message.finalizing !== true) return null;

    const loadingLabel = this.getLabel(
      "followUpsLoading",
      "Vervolgvragen worden voorbereid",
    );
    return html`<div
      class="follow-up-skeleton"
      role="status"
      aria-label=${loadingLabel}
    >
      <afas-skeleton
        variant="rectangle"
        width="180px"
        height="34px"
      ></afas-skeleton>
    </div>`;
  }

  private renderDisclaimer() {
    return html`<div class="disclaimer">
      ${this.getLabel("disclaimer", DEFAULT_DISCLAIMER_TEXT)}
    </div>`;
  }

  /**
   * Stands until the visitor closes it, and stays closed for the rest of the browser
   * session. The warning is about the product rather than about any one chat, so it is
   * not tracked per conversation. The version in the key re-opens it for everyone once
   * the wording changes materially.
   */
  private isPrivacyNoticeVisible(): boolean {
    return !this.privacyNoticeDismissed;
  }

  private dismissPrivacyNotice(): void {
    this.privacyNoticeDismissed = true;
    writePrivacyNoticeDismissed();
  }

  private shouldShowDisclaimer(
    message: ChatMessage,
    index: number,
    messages: ChatMessage[],
  ): boolean {
    // The visible answer is "settled" once the turn completes or reaches the finalizing
    // stage (follow-ups still streaming); the disclaimer appears as soon as it settles,
    // even though the turn is still technically generating its follow-ups.
    const settled = !message.pending || message.finalizing === true;
    return (
      message.role === "assistant" &&
      settled &&
      !message.fallback &&
      !message.canceled &&
      index === messages.length - 1 &&
      (!this.isGenerating() || message.finalizing === true)
    );
  }

  // An answer is clamped into a short preview only once TWO later answers exist,
  // so the two newest answers always stay in full: the streaming/pending answer
  // and the most recent completed answer. The completed one is the answer the
  // auto-scroll anchors on while a new answer generates (see
  // scrollAnswerContextIntoView) — clamping it would move its bottom edge
  // mid-generation and break the follow. Keeping it open also means a new
  // question collapses the answer ABOVE the anchor, so the collapse still
  // coincides with asking a question and never disturbs the anchor. Questions
  // stay in full; fallback panels render their own component and never count as
  // a later answer or get clamped here.
  private shouldClampMessage(
    message: ChatMessage,
    index: number,
    messages: ChatMessage[],
  ): boolean {
    if (message.role !== "assistant" || message.fallback) return false;
    let laterAnswers = 0;
    for (let i = index + 1; i < messages.length; i++) {
      const later = messages[i];
      if (later?.role === "assistant" && !later.fallback) {
        laterAnswers += 1;
        if (laterAnswers >= 2) return true;
      }
    }
    return false;
  }

  private renderPreparedQuestion(
    preparedQuestion: ChatState["preparedQuestion"],
    busy: boolean,
  ) {
    if (!preparedQuestion) return null;

    return html`
      <section
        class="prepared-question"
        aria-label=${this.getLabel(
          "preparedQuestionLabel",
          "Voorbereide vraag",
        )}
      >
        <div class="prepared-question__content">
          <p class="prepared-question__text">${preparedQuestion.question}</p>
          <afas-button
            variant="primary"
            .label=${this.getLabel(
              "preparedQuestionAction",
              this.preparedQuestionActionLabel,
            )}
            ?disabled=${busy}
            @click=${() => this.startPreparedQuestion()}
          ></afas-button>
        </div>
      </section>
    `;
  }

  private schedulePendingAnswerScroll(): void {
    if (this.answerScrollFrame !== 0) {
      window.cancelAnimationFrame(this.answerScrollFrame);
    }

    this.answerScrollFrame = window.requestAnimationFrame(() => {
      this.answerScrollFrame = window.requestAnimationFrame(() => {
        this.answerScrollFrame = 0;
        this.scrollPendingAnswerIntoView();
      });
    });
  }

  private scrollPendingAnswerIntoView(): void {
    const pendingAnswer = this.getPendingAssistantMessage();
    if (!pendingAnswer) {
      if (!this.isGenerating()) {
        this.userInterruptedAnswerScroll = false;
      }
      this.scrollNewFallbackIntoView();
      return;
    }

    if (this.userInterruptedAnswerScroll) return;

    // Follow the streaming answer all the way to its end (#153). We stick to the
    // bottom of the thread rather than pinning the previous answer's tail (and
    // the user's question) at the top, which stopped the follow halfway and felt
    // broken. Re-applying on every update and content resize lets late-rendered
    // content (images, code, tables) keep the newest text in view; the follow
    // eases (smooth) the whole time.
    this.followAnswerToBottom();
  }

  private followAnswerToBottom(): void {
    const messagesElement = this.getMessagesElement();
    if (!messagesElement) return;

    const scrollBehavior: ScrollBehavior = prefersReducedMotion()
      ? "auto"
      : "smooth";
    const scrollTop = this.clampScrollTop(
      messagesElement,
      messagesElement.scrollHeight,
    );

    // Skip no-op corrections so streaming reflow does not retarget the smooth
    // scroll every frame; only move once we have actually drifted from the bottom.
    if (Math.abs(messagesElement.scrollTop - scrollTop) <= 1) return;

    messagesElement.scrollTo({
      top: scrollTop,
      behavior: scrollBehavior,
    });
  }

  // Remember that real thread content has rendered without a live fallback. This is
  // what tells a fallback that APPEARS live (after a failed question, in a thread
  // that already showed messages) apart from one RESTORED on a page refresh, where
  // the very first populated render of this fresh component instance is already the
  // fallback. Only the live case auto-scrolls; the refresh case defers to the saved
  // scroll position.
  private trackContentBeforeFallback(): void {
    const messages = this.state?.messages ?? [];
    if (messages.length > 0 && !isThreadInFallbackMode(messages)) {
      this.sawContentBeforeFallback = true;
    }
  }

  // Bring a freshly-appeared AI-unavailable fallback into view once, the same way an
  // answer is anchored (previous answer's tail at the top, the fallback below). Runs
  // from the double-rAF scroll schedule so the fallback panel has laid out.
  private scrollNewFallbackIntoView(): void {
    const fallback = getLiveFallbackMessage(this.state?.messages ?? []);
    if (!fallback) {
      // Recovered/frozen or never present: allow a future fallback to scroll again.
      this.fallbackScrollHandledId = "";
      return;
    }
    // Already handled this fallback: do not re-scroll when its sources are refined,
    // the banner re-renders, or the silent reconnect probe ticks.
    if (this.fallbackScrollHandledId === fallback.id) return;
    this.fallbackScrollHandledId = fallback.id;
    // Restored on refresh: keep the user's saved scroll position.
    if (!this.sawContentBeforeFallback) return;
    if (this.userInterruptedAnswerScroll) return;

    const anchor = this.getAnswerContextAnchor(fallback);
    if (!anchor) return;
    this.scrollAnswerContextIntoView(anchor);
  }

  private ensureContentResizeObserver(): void {
    if (this.contentResizeObserver || typeof ResizeObserver === "undefined") {
      return;
    }
    const inner =
      this.shadowRoot?.querySelector<HTMLElement>(".messages-inner");
    if (!inner) return;

    // Streaming answers grow and reflow without always triggering a Lit render
    // (images and syntax highlighting settle asynchronously). Re-run the
    // auto-follow whenever the content box resizes so it keeps up with the tail.
    this.contentResizeObserver = new ResizeObserver(() => {
      if (!this.isGenerating() || this.userInterruptedAnswerScroll) return;
      this.schedulePendingAnswerScroll();
    });
    this.contentResizeObserver.observe(inner);
  }

  private getAnswerContextAnchor(
    pendingAnswer: ChatMessage,
  ): { element: HTMLElement; edge: "top" | "bottom" } | null {
    const messages = this.state?.messages ?? [];
    const pendingIndex = messages.findIndex(
      (message) => message.id === pendingAnswer.id,
    );
    if (pendingIndex < 0) return null;

    // Prefer the bottom edge of the previous complete answer: scrolling along
    // until it is just barely visible keeps the last Q&A in context and the
    // user's own question on screen above the streaming answer.
    for (let index = pendingIndex - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message?.role === "assistant" && !message.pending) {
        const element = this.getMessageAnchor(message.id);
        if (element) return { element, edge: "bottom" };
        break;
      }
    }

    // First turn (no previous answer): pin the user's question to the top so it
    // stays visible while the answer generates below it.
    const question = messages[pendingIndex - 1];
    const questionElement = question
      ? this.getMessageAnchor(question.id)
      : null;
    if (questionElement) return { element: questionElement, edge: "top" };

    const answerElement = this.getMessageAnchor(pendingAnswer.id);
    return answerElement ? { element: answerElement, edge: "top" } : null;
  }

  private scrollAnswerContextIntoView(anchor: {
    element: HTMLElement;
    edge: "top" | "bottom";
  }): void {
    // Ease the follow (smooth) the whole time so the view glides along with the
    // streaming answer instead of snapping on every token/reflow; honour
    // reduced-motion by falling back to an instant correction.
    const scrollBehavior: ScrollBehavior = prefersReducedMotion()
      ? "auto"
      : "smooth";

    const messagesElement = this.getMessagesElement();
    if (!messagesElement) {
      anchor.element.scrollIntoView({
        block: "start",
        behavior: scrollBehavior,
      });
      return;
    }

    const rect = anchor.element.getBoundingClientRect();
    const anchorEdge = anchor.edge === "bottom" ? rect.bottom : rect.top;
    const messagesTop = messagesElement.getBoundingClientRect().top;
    // Bottom-edge anchor: leave a thin tail of the previous answer visible at the
    // top. Top-edge anchor (first turn): align the question flush to the top.
    const topOffset =
      anchor.edge === "bottom" ? PREVIOUS_ANSWER_VISIBLE_TAIL_PX : 0;
    const desiredScrollTop =
      messagesElement.scrollTop + anchorEdge - messagesTop - topOffset;
    const scrollTop = this.clampScrollTop(messagesElement, desiredScrollTop);

    // Skip no-op corrections so streaming reflow does not retarget the smooth
    // scroll every frame; only move once we have actually drifted from the anchor.
    if (Math.abs(messagesElement.scrollTop - scrollTop) <= 1) return;

    messagesElement.scrollTo({
      top: scrollTop,
      behavior: scrollBehavior,
    });
  }

  private clampScrollTop(
    messagesElement: HTMLElement,
    scrollTop: number,
  ): number {
    const maxScrollTop = Math.max(
      0,
      messagesElement.scrollHeight - messagesElement.clientHeight,
    );
    return Math.min(Math.max(scrollTop, 0), maxScrollTop);
  }

  private handleMessagesUserScroll(event: Event): void {
    if (!this.isGenerating() || this.userInterruptedAnswerScroll) return;

    // Detecting "the user took over" from raw scroll events is unreliable: our
    // own eased follow, content streaming in below, and an earlier answer
    // collapsing above all move scrollTop and fire scroll events that are
    // indistinguishable from a user drag by position alone (the source of the
    // false interrupts that killed the follow). Instead we react ONLY to genuine
    // input gestures, which the browser never synthesises for programmatic
    // scrolling or layout reflow, so the follow is handed over by the user and
    // nothing else -- completely independent of scroll timing.
    //
    // wheel / touch / keyboard are always a deliberate hand-off. A pointerdown
    // counts only when it lands on the scrollbar gutter (a scrollbar drag);
    // clicking or selecting text inside the streaming answer must not stop the
    // follow.
    if (
      event.type === "pointerdown" &&
      !this.isScrollbarPointerDown(event as PointerEvent)
    ) {
      return;
    }

    this.userInterruptedAnswerScroll = true;
  }

  // A pointerdown hands the scroll over only when it lands in the vertical
  // scrollbar gutter (to the right of the content box), so dragging the scrollbar
  // interrupts the follow while clicking a link or selecting text in the answer
  // does not.
  private isScrollbarPointerDown(event: PointerEvent): boolean {
    const messagesElement = this.getMessagesElement();
    if (!messagesElement) return false;
    const rect = messagesElement.getBoundingClientRect();
    return event.clientX - rect.left >= messagesElement.clientWidth;
  }

  private restoreThreadScrollAfterRefresh(): void {
    const sessionToken = this.state?.sessionToken ?? "";
    if (!sessionToken || this.restoredScrollSessionToken === sessionToken) {
      return;
    }
    // Only the first conversation shown after a page load restores its saved scroll (a
    // genuine refresh). An in-page switch to another conversation is handled by
    // resetScrollOnConversationSwitch, which jumps to the latest message instead of
    // reviving the previous position (issue #116). Mark it handled so no snapshot
    // restore fires for the switched-in conversation.
    if (
      this.lastConversationToken &&
      this.lastConversationToken !== sessionToken
    ) {
      this.restoredScrollSessionToken = sessionToken;
      return;
    }
    if ((this.state?.messages.length ?? 0) === 0 || this.isGenerating()) {
      return;
    }

    const savedScroll = readThreadScrollSnapshot();
    if (!savedScroll || savedScroll.sessionToken !== sessionToken) {
      this.restoredScrollSessionToken = sessionToken;
      return;
    }
    if (this.restoreScrollFrame !== 0) return;

    this.restoreScrollFrame = window.requestAnimationFrame(() => {
      this.restoreScrollFrame = window.requestAnimationFrame(() => {
        this.restoreScrollFrame = 0;
        if (this.state?.sessionToken !== sessionToken) return;

        const messagesElement = this.getMessagesElement();
        if (!messagesElement) return;

        messagesElement.scrollTop = this.clampScrollTop(
          messagesElement,
          savedScroll.scrollTop,
        );
        this.restoredScrollSessionToken = sessionToken;
      });
    });
  }

  private saveThreadScroll(): void {
    const sessionToken = this.state?.sessionToken ?? "";
    const messagesElement = this.getMessagesElement();
    if (!sessionToken || !messagesElement || this.isInitialChat()) {
      clearThreadScrollSnapshot();
      return;
    }

    const scrollTop = Math.round(messagesElement.scrollTop);
    if (scrollTop <= 0) {
      clearThreadScrollSnapshot();
      return;
    }

    writeThreadScrollSnapshot({ sessionToken, scrollTop });
  }

  // Switching to another conversation in-page reuses the same .messages scroll
  // container, so its scrollTop would otherwise carry over from the previous chat
  // (issue #116: going to another chat and back kept the old scroll level). Jump to the
  // latest message on every open (WhatsApp-style) EXCEPT a genuine page refresh of the
  // same conversation, which restoreThreadScrollAfterRefresh restores to the saved scroll.
  private resetScrollOnConversationSwitch(): void {
    const sessionToken = this.state?.sessionToken ?? "";
    // Ignore the transient empty token while a resume is loading; settle only on the
    // real conversation id so the switch is detected on the id that actually lands.
    if (!sessionToken || sessionToken === this.lastConversationToken) return;
    const previous = this.lastConversationToken;
    this.lastConversationToken = sessionToken;
    // The first conversation shown in this instance is a page load. Only a real refresh has a
    // saved scroll snapshot for that exact conversation; leave those to the refresh-restore.
    // Opening a conversation from the list (no snapshot) still jumps to the latest message.
    if (!previous) {
      const saved = readThreadScrollSnapshot();
      if (saved && saved.sessionToken === sessionToken) return;
    }

    const scrollToLatest = () => {
      const messagesElement = this.getMessagesElement();
      if (!messagesElement || this.state?.sessionToken !== sessionToken) return;
      messagesElement.scrollTop = messagesElement.scrollHeight;
    };
    // Two frames so the switched-in conversation has laid out and scrollHeight is real.
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(scrollToLatest),
    );
  }

  private getMessagesElement(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>(".messages") ?? null;
  }

  private getPendingAssistantMessage(): ChatMessage | null {
    return (
      this.state?.messages.find(
        (message) => message.role === "assistant" && message.pending,
      ) ?? null
    );
  }

  private getMessageAnchor(messageId: string): HTMLElement | null {
    return (
      Array.from(
        this.shadowRoot?.querySelectorAll<HTMLElement>("[data-message-id]") ??
          [],
      ).find((element) => element.dataset.messageId === messageId) ?? null
    );
  }

  private startPreparedQuestion(): void {
    const preparedQuestion = this.state?.preparedQuestion;
    if (!preparedQuestion || this.isBusy()) return;
    this.dispatchEvent(
      new CustomEvent("jonas-prepared-question-start", {
        detail: {
          question: preparedQuestion.question,
          entryPoint: preparedQuestion.entryPoint,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private isBusy(): boolean {
    return (
      this.state?.status === "starting" ||
      this.state?.status === "streaming" ||
      this.state?.isRestoring === true
    );
  }

  private isGenerating(): boolean {
    return (
      this.state?.status === "starting" || this.state?.status === "streaming"
    );
  }

  private getLabel(key: string, fallback: string): string {
    const labels = this.state?.labels;
    const value = labels?.[key]?.trim();
    return value || fallback;
  }
}

function prefersReducedMotion(): boolean {
  return (
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

function readThreadScrollSnapshot(): {
  sessionToken: string;
  scrollTop: number;
} | null {
  try {
    const raw = window.sessionStorage.getItem(THREAD_SCROLL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const sessionToken = stringValue(parsed.sessionToken);
    const scrollTop = Number(parsed.scrollTop);
    if (!sessionToken || !Number.isFinite(scrollTop) || scrollTop <= 0) {
      return null;
    }
    return { sessionToken, scrollTop };
  } catch {
    return null;
  }
}

function writeThreadScrollSnapshot(snapshot: {
  sessionToken: string;
  scrollTop: number;
}): void {
  try {
    window.sessionStorage.setItem(
      THREAD_SCROLL_STORAGE_KEY,
      JSON.stringify(snapshot),
    );
  } catch {
    // Scroll restoration is best-effort when sessionStorage is unavailable.
  }
}

function clearThreadScrollSnapshot(): void {
  try {
    window.sessionStorage.removeItem(THREAD_SCROLL_STORAGE_KEY);
  } catch {
    // Scroll restoration is best-effort when sessionStorage is unavailable.
  }
}

function readPrivacyNoticeDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(PRIVACY_NOTICE_STORAGE_KEY) === "1";
  } catch {
    // Unreadable storage must not swallow a warning: show it.
    return false;
  }
}

function writePrivacyNoticeDismissed(): void {
  try {
    window.sessionStorage.setItem(PRIVACY_NOTICE_STORAGE_KEY, "1");
  } catch {
    // It stays closed for this page either way; only the next page load shows it again.
  }
}

function stringValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || typeof value === "undefined") return "";
  return String(value);
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-chat-thread": HelpJonasChatThread;
  }
}
